import { CONSTRAINT_NAMES, type KnownType, type PuzzleDoc } from '@zinc/shared';
import { useEffect, useState } from 'react';
import { callCore, coreAvailable } from '../../lib/core/client';
import { ChevronDown } from './icons';

/** "Normal sudoku · Killer · Kropki · Thermo": each rule once, in the puzzle's order. */
export function rulesSummary(rules: string, types: string[]): string {
  if (types.length > 0) {
    const names = [...new Set(types.map(labelFor))];
    return ['Normal sudoku', ...names].join(' · ');
  }
  const first = rules.split(/\n|\.\s/)[0]?.trim();
  return first || 'Normal sudoku rules apply.';
}

export function labelFor(type: string): string {
  const known = (CONSTRAINT_NAMES as Partial<Record<string, string>>)[type as KnownType];
  if (known) return known;
  const t = type.replace(/[-_]/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

interface RuleList {
  rules: { kind: string; text: string }[];
  setterWords: boolean;
}

/** A rule's name on the list: its type's, with the two the core adds. */
function ruleName(kind: string): string {
  if (kind === 'normal') return 'Normal sudoku';
  if (kind === 'fog') return 'Fog';
  return labelFor(kind);
}

/** One row of the list: the name, and its words when tapped. */
function RuleRow(props: { name: string; text: string; keepLines?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="border-t border-line first:border-t-0">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex min-h-10 w-full items-center gap-2 border-0 bg-transparent p-0 text-left text-[14px] font-semibold text-ink"
      >
        <span className="min-w-0 flex-1">{props.name}</span>
        <span className={`transition-transform ${open ? 'rotate-180' : ''}`}>
          <ChevronDown />
        </span>
      </button>
      {open && (
        <p
          className={`m-0 pb-2.5 text-[14px] leading-relaxed text-muted ${props.keepLines ? 'whitespace-pre-wrap' : ''}`}
        >
          {props.text}
        </p>
      )}
    </li>
  );
}

/** The rules as names, each opening to its words (Marko, 2026-11-02: he knows them by name). A
 * setter's own words are one more row: that's where a puzzle's twist is. Until the core answers,
 * or without it, the rules text as it is. */
export function RuleNames({ doc }: { doc: PuzzleDoc }) {
  const [list, setList] = useState<RuleList | null>(null);
  useEffect(() => {
    if (!coreAvailable()) return;
    let current = true;
    callCore<RuleList>('rule_list', doc)
      .then((r) => {
        if (current) setList(r);
      })
      .catch(() => {});
    return () => {
      current = false;
    };
  }, [doc]);
  const words = doc.meta.rules.trim() || NORMAL;
  if (!list) return <div className="whitespace-pre-wrap">{words}</div>;
  // Rule types can share a name: one row, saying what each says.
  const rows = new Map<string, string>();
  for (const r of list.rules) {
    const name = ruleName(r.kind);
    rows.set(name, [rows.get(name), r.text].filter(Boolean).join(' '));
  }
  return (
    <ul className="m-0 flex list-none flex-col p-0" aria-label="Rules, by name">
      {[...rows].map(([name, text]) => (
        <RuleRow key={name} name={name} text={text} />
      ))}
      {list.setterWords && <RuleRow name="Setter’s words" text={words} keepLines />}
    </ul>
  );
}

const NORMAL = 'Normal sudoku rules apply: place each digit once in every row, column and box.';

export function RulesStrip(props: {
  doc: PuzzleDoc;
  summary: string;
  open?: boolean;
  onToggle?: () => void;
  side?: React.ReactNode;
  /** Open over the grid (phone) instead of in the page flow (desktop side panel). */
  overlay?: boolean;
}) {
  const [localOpen, setLocalOpen] = useState(false);
  const open = props.open ?? localOpen;
  const toggle = props.onToggle ?? (() => setLocalOpen((o) => !o));
  const overlay = props.overlay ?? true;
  return (
    <div className="relative flex shrink-0 flex-col gap-2 px-3">
      <div className="flex gap-2">
        <button
          type="button"
          aria-expanded={open}
          onClick={toggle}
          className="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-[12px] border border-line bg-surface px-3 text-left text-[13px] text-ink"
        >
          <span className="font-semibold">Rules</span>
          <span className="min-w-0 flex-1 truncate text-muted">{props.summary}</span>
          <span className={`transition-transform ${open ? 'rotate-180' : ''}`}>
            <ChevronDown />
          </span>
        </button>
        {props.side}
      </div>
      {open && overlay && (
        <>
          <button
            type="button"
            aria-label="Close the rules"
            onClick={toggle}
            className="fixed inset-0 z-30 cursor-default bg-[rgba(31,30,28,0.28)]"
          />
          <div
            role="region"
            aria-label="Rules"
            className="absolute top-12 right-3 left-3 z-40 max-h-[60vh] overflow-auto rounded-[12px] border border-line bg-surface px-3.5 py-2 text-[14px] leading-relaxed shadow-[0_8px_24px_rgba(31,30,28,0.16)]"
          >
            <RuleNames doc={props.doc} />
          </div>
        </>
      )}
      {open && !overlay && (
        <div className="max-h-[40vh] overflow-auto rounded-[12px] border border-line bg-surface px-3.5 py-2 text-[14px] leading-relaxed">
          <RuleNames doc={props.doc} />
        </div>
      )}
    </div>
  );
}
